import {isArray} from 'lodash';
import React, {FC, memo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ErrorWithData} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AsyncData} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {AsyncLoader} from '../../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../../models/api/activityModel';
import {ConversationSummaryCommentViewerContainer} from './conversationSummaryCommentViewerContainer';
import {StyledEmptyDiv, StyledLoaderDiv} from './conversationSummaryCommonStyles';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';

/*
 * Props.
 */

export type ConversationSummaryPinnedActivityClickHandler = (
  activityId: number,
  conversationId: number,
) => void;

interface ConversationSummaryPinnedActivitiesProps {
  panelId: ViewerPanelId;
  pinnedOrRecentlyUnpinnedActivities: AsyncData<ReadonlyArray<ActivityModel>>;
  onPinnedActivitiesRetryClick: () => void;
  onJumpClick: ConversationSummaryPinnedActivityClickHandler;
  onPinClick: ConversationSummaryPinnedActivityClickHandler;
  onUnpinClick: ConversationSummaryPinnedActivityClickHandler;
  sectionType: ConversationSummarySectionsEnum;
}

/*
 * Style.
 */

const StyledPinnedActivitiesDiv = styled.div`
  display: grid;
  grid-row-gap: 7px;
  grid-column: 2;
`;

const StyledNoPinnedActivitiesDiv = styled(StyledEmptyDiv)`
  padding-left: 4px;
`;

const StyledPinnedActivitiesContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledPinnedActivityDiv = styled.div`
  padding: 3px 0px;
`;

/*
 * Component.
 */

export const ConversationSummaryPinnedActivities: FC<ConversationSummaryPinnedActivitiesProps> = memo(
  (props) => (
    <StyledPinnedActivitiesDiv data-testid={props.sectionType}>
      {renderPinnedActivitiesContent(props)}
    </StyledPinnedActivitiesDiv>
  ),
);

function renderPinnedActivitiesContent(props: ConversationSummaryPinnedActivitiesProps) {
  const {pinnedOrRecentlyUnpinnedActivities, onPinnedActivitiesRetryClick} = props;

  if (pinnedOrRecentlyUnpinnedActivities.status !== AsyncStatusesEnum.LOADED) {
    return renderLoader(pinnedOrRecentlyUnpinnedActivities.status, onPinnedActivitiesRetryClick);
  }

  if (
    !isArray(pinnedOrRecentlyUnpinnedActivities.data) ||
    pinnedOrRecentlyUnpinnedActivities.data.length === 0
  ) {
    return (
      <StyledNoPinnedActivitiesDiv data-testid="conversationSummaryNoPinnedActivities">
        {renderNoPinnedActivitiesMessage(props.sectionType)}
      </StyledNoPinnedActivitiesDiv>
    );
  }

  const {panelId, onJumpClick, onPinClick, onUnpinClick} = props;
  return renderPinnedActivities(
    pinnedOrRecentlyUnpinnedActivities.data,
    panelId,
    onJumpClick,
    onPinClick,
    onUnpinClick,
  );
}

function renderLoader(
  pinnedCommentsLoadingStatus: AsyncStatusesEnum,
  onPinnedCommentsRetryClick: () => void,
) {
  return (
    <StyledLoaderDiv>
      <AsyncLoader
        onRetryClick={onPinnedCommentsRetryClick}
        size={VisualSizesEnum.SMALL}
        status={pinnedCommentsLoadingStatus}
      />
    </StyledLoaderDiv>
  );
}

function renderNoPinnedActivitiesMessage(sectionType: ConversationSummarySectionsEnum) {
  switch (sectionType) {
    case ConversationSummarySectionsEnum.PINNED_ACTIVITIES:
      return <Trans id="conversation-summary-pinned-activities-none">No pinned comments</Trans>;
    default:
      return null;
  }
}

function renderPinnedActivities(
  pinnedOrRecentlyUnpinnedActivities: ReadonlyArray<ActivityModel>,
  panelId: ViewerPanelId,
  onJumpClick: ConversationSummaryPinnedActivityClickHandler,
  onPinClick: ConversationSummaryPinnedActivityClickHandler,
  onUnpinClick: ConversationSummaryPinnedActivityClickHandler,
) {
  return (
    <StyledPinnedActivitiesContainerDiv>
      {pinnedOrRecentlyUnpinnedActivities.map((activity) => (
        <StyledPinnedActivityDiv key={activity.id}>
          {renderPinnedActivity(activity, panelId, onJumpClick, onPinClick, onUnpinClick)}
        </StyledPinnedActivityDiv>
      ))}
    </StyledPinnedActivitiesContainerDiv>
  );
}

function renderPinnedActivity(
  pinnedActivity: ActivityModel,
  panelId: ViewerPanelId,
  onJumpClick: ConversationSummaryPinnedActivityClickHandler,
  onPinClick: ConversationSummaryPinnedActivityClickHandler,
  onUnpinClick: ConversationSummaryPinnedActivityClickHandler,
) {
  switch (pinnedActivity.visualType) {
    case ActivityVisualTypesEnum.COMMENT:
      return (
        <ConversationSummaryCommentViewerContainer
          activity={pinnedActivity}
          panelId={panelId}
          onJumpClick={onJumpClick}
          onPinClick={onPinClick}
          onUnpinClick={onUnpinClick}
        />
      );

    default:
      reportToBugsnag(
        new ErrorWithData('Tried to render pinned activity of an unsupported type', {
          activity: pinnedActivity,
        }),
      );
      return null;
  }
}
